import { CommonModule } from '@angular/common';
import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { NavbarComponent, IconComponent, FooterComponent } from '@/shared/components';
import { ContestService } from '@/services/contest.service';
import { Contest, ContestStage, ContestPosition, ContestDocument } from '@/models/contest.model';
import { EtapaFormatService } from '@/services/etapa-format.service';

@Component({
  selector: 'app-contest-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NavbarComponent, IconComponent, FooterComponent],
  templateUrl: './contest-detail.component.html',
})
export class ContestDetailComponent implements OnInit {
  concursoId: number = 0;
  contest: Contest | null = null;
  /** Documentos já visíveis no site (dataExibicaoSite nula ou já passou). */
  visibleDocuments: ContestDocument[] = [];
  isLoading = false;
  errorMessage = '';

  // Controle do botão fixo
  showStickyButton = false;
  private mobileCtaElement: HTMLElement | null = null;

  // Setter para capturar o elemento assim que ele for renderizado pelo *ngIf
  @ViewChild('mobileCta') set content(content: ElementRef) {
    if (content) {
      this.mobileCtaElement = content.nativeElement;
    }
  }

  // Ouvinte de scroll para verificar a posição em tempo real
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (!this.mobileCtaElement) return;

    const rect = this.mobileCtaElement.getBoundingClientRect();

    // Altura da navbar (aproximadamente 64px ou 4rem)
    // Se a parte de BAIXO do elemento (rect.bottom) for menor que a altura da navbar,
    // significa que ele já rolou para cima e sumiu atrás da navbar.
    const navbarHeight = 80;

    // Atualiza o estado: true se rolou para fora, false se ainda está visível
    this.showStickyButton = rect.bottom < navbarHeight;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contestService: ContestService,
    private sanitizer: DomSanitizer,
    private etapaFormatService: EtapaFormatService
  ) { }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.concursoId = parseInt(id);
      this.loadContestDetails();
    }
  }

  loadContestDetails() {
    this.isLoading = true;
    this.contestService.getPublicContestById(this.concursoId).subscribe({
      next: (contest) => {
        // Lógica de Ordenação das Etapas
        if (contest && contest.etapas) {
          contest.etapas.sort((a, b) => {
            const startA = new Date(a.dtInicio).getTime();
            const startB = new Date(b.dtInicio).getTime();
            
            // Primeiro compara data de início
            if (startA !== startB) {
              return startA - startB;
            }
            
            // Se início for igual, compara data de fim
            const endA = new Date(a.dtFim).getTime();
            const endB = new Date(b.dtFim).getTime();
            return endA - endB;
          });
        }

        this.contest = contest;
        this.visibleDocuments = this.filterVisibleDocuments(contest?.documentos ?? []);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading contest:', error);
        this.errorMessage = 'Erro ao carregar dados do edital';
        this.isLoading = false;
      }
    });
  }

  canRegister(): boolean {
    return this.contest?.tag === 'EM_ANDAMENTO';
  }

  getRegistrationStatus(): { title: string; description: string; button: string; disabled: boolean } {
    // Verifica se as inscrições estão congeladas
    if (this.contest?.isInscricoesCongeladas) {
      return {
        title: 'Inscrições congeladas',
        description: 'As inscrições estão temporariamente congeladas para este edital. Tente novamente mais tarde.',
        button: 'Inscrições Congeladas',
        disabled: true,
      };
    }

    // Verifica as datas da etapa de inscrição
    const etapaInscricao = this.getInscriptionStage();
    if (etapaInscricao) {
      const antesDoInicio = this.compareDateWithNow(etapaInscricao.dtInicio, 'before');
      const depoisDoFim = this.compareDateWithNow(etapaInscricao.dtFim, 'after');

      if (antesDoInicio) {
        return {
          title: 'Inscrições ainda não Abertas',
          description: 'As inscrições para este concurso ainda não estão abertas. Fique atento às datas de inscrição.',
          button: 'Em Breve',
          disabled: true,
        };
      }

      if (depoisDoFim) {
        return {
          title: 'Inscrições Encerradas',
          description: 'As inscrições para este concurso foram encerradas. Agradecemos o seu interesse.',
          button: 'Inscrições Encerradas',
          disabled: true,
        };
      }
    }

    switch (this.contest?.tag) {
      case 'FUTURO':
        return {
          title: 'Inscrições ainda não Abertas',
          description: 'As inscrições para este concurso ainda não estão abertas. Fique atento às datas de inscrição.',
          button: 'Indisponível',
          disabled: true,
        };
      case 'EM_ANDAMENTO':
      case 'INSCRICOES_ABERTAS':
        return {
          title: 'Inscrições Abertas',
          description: 'As inscrições para este concurso estão atualmente abertas. Não perca a oportunidade de participar!',
          button: 'Inscreva-se Agora',
          disabled: false,
        };
      case 'FINALIZADO':
        return {
          title: 'Inscrições Encerradas',
          description: 'As inscrições para este concurso foram encerradas. Agradecemos o seu interesse.',
          button: 'Inscrições Encerradas',
          disabled: true,
        };
      default:
        return {
          title: 'Inscrições Indisponíveis',
          description: 'Status do concurso não disponível.',
          button: 'Indisponível',
          disabled: true,
        };
    }
  }

  getStatusColor(tag: string): string {
    const etapaInscricao = this.getInscriptionStage();
    if (etapaInscricao) {
      const antesDoInicio = this.compareDateWithNow(etapaInscricao.dtInicio, 'before');
      const depoisDoFim = this.compareDateWithNow(etapaInscricao.dtFim, 'after');
      if (antesDoInicio) {
        return 'bg-blue-500';
      }
      if (depoisDoFim) {
        return 'bg-gray-500';
      }
    }

    switch (tag) {
      case 'FUTURO': return 'bg-blue-500';
      case 'EM_ANDAMENTO':
      case 'INSCRICOES_ABERTAS': return 'bg-green-500';
      case 'FINALIZADO': return 'bg-[#f37835]';
      default: return 'bg-gray-500';
    }
  }

  getStatusLabel(tag: string): string {
    const etapaInscricao = this.getInscriptionStage();
    if (etapaInscricao) {
      const antesDoInicio = this.compareDateWithNow(etapaInscricao.dtInicio, 'before');
      const depoisDoFim = this.compareDateWithNow(etapaInscricao.dtFim, 'after');
      if (antesDoInicio) return 'Em Breve';
      if (depoisDoFim) return 'Inscrições Encerradas';
    }

    switch (tag) {
      case 'FUTURO': return 'Em Breve';
      case 'EM_ANDAMENTO':
      case 'INSCRICOES_ABERTAS': return 'Inscrições Abertas';
      case 'FINALIZADO': return 'Finalizado';
      default: return 'Desconhecido';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '-';
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, year, month, day, hours, minutes] = isoMatch;
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  private extractTipoCodigo(tipo: string | any): string {
    if (typeof tipo === 'object' && tipo !== null) {
      return tipo.codigo || tipo.nome || '';
    }
    return tipo || '';
  }

  getInscriptionStage(): ContestStage | undefined {
    return this.contest?.etapas.find(etapa => this.extractTipoCodigo(etapa.tipo) === 'INSCRICAO');
  }

  getPaymentDeadlineStage(): ContestStage | undefined {
    return this.contest?.etapas.find(etapa => this.extractTipoCodigo(etapa.tipo) === 'PRAZO_PAGAMENTO');
  }

  getExamStage(): ContestStage | undefined {
    const tipoCodigo = (etapa: ContestStage) => this.extractTipoCodigo(etapa.tipo);
    return this.contest?.etapas.find(etapa => {
      const codigo = tipoCodigo(etapa);
      return codigo === 'PROVA_OBJETIVA' || codigo === 'PROVA';
    });
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  getRegistrationFeeRange(): string {
    if (!this.contest || this.contest.cargos.length === 0) return '-';
    if (this.contest.inscricoesGratuitas) return 'Gratuito';
    const fees = this.contest.cargos
      .map(cargo => cargo.taxaInscricao)
      .filter(fee => fee !== null && fee !== undefined) as number[];
    if (fees.length === 0) return 'Gratuito';
    const minFee = Math.min(...fees);
    const maxFee = Math.max(...fees);
    if (minFee === maxFee) return this.formatCurrency(minFee);
    return `${this.formatCurrency(minFee)} ~ ${this.formatCurrency(maxFee)}`;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  getDocumentTypeLabel(tipo: string): string {
    const types: { [key: string]: string } = {
      'EDITAL': 'Edital',
      'CRONOGRAMA': 'Cronograma',
      'RETIFICACAO': 'Retificação',
      'ANEXO': 'Anexo',
      'RESULTADO': 'Resultado'
    };
    return types[tipo] || tipo;
  }

  /** Data a exibir: quando programado usa dataExibicaoSite (hora disponibilizada), senão criadoEm. */
  getDocumentDisplayDate(document: ContestDocument): string | undefined {
    if (document.dataExibicaoSite) return document.dataExibicaoSite;
    return document.criadoEm;
  }

  /**
   * Filtra documentos já visíveis no site:
   * - sem dataExibicaoSite (exibição imediata) ou
   * - dataExibicaoSite já passou (<= agora).
   */
  private filterVisibleDocuments(documentos: ContestDocument[]): ContestDocument[] {
    if (!documentos?.length) return [];
    const now = new Date();
    return documentos.filter((doc) => {
      if (!doc.dataExibicaoSite) return true;
      const exibirEm = new Date(doc.dataExibicaoSite);
      return exibirEm.getTime() <= now.getTime();
    });
  }

  getStageTypeLabel(tipo: string | any): string {
    if (typeof tipo === 'object' && tipo !== null && tipo.nome) return tipo.nome;
    let tipoCodigo = (typeof tipo === 'object' && tipo !== null) ? (tipo.codigo || '') : (tipo || '');
    return this.etapaFormatService.formatEtapaSync(tipoCodigo);
  }

  private compareDateWithNow(dateString: string, comparison: 'before' | 'after' | 'equal'): boolean {
    const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    let targetDate: Date;

    if (isoMatch) {
      const [, year, month, day, hours, minutes, seconds] = isoMatch;
      targetDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes), parseInt(seconds));
    } else {
      targetDate = new Date(dateString);
    }

    const now = new Date();
    if (comparison === 'before') return now < targetDate;
    if (comparison === 'after') return now > targetDate;
    return now.getFullYear() === targetDate.getFullYear() && now.getMonth() === targetDate.getMonth() && now.getDate() === targetDate.getDate();
  }

  isStageActive(stage: ContestStage): boolean {
    return !this.compareDateWithNow(stage.dtInicio, 'before') && !this.compareDateWithNow(stage.dtFim, 'after');
  }

  isStagePast(stage: ContestStage): boolean {
    return this.compareDateWithNow(stage.dtFim, 'after');
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'em-andamento': return 'bg-blue-500 text-white';
      case 'concluida': return 'bg-green-500 text-white';
      case 'inscricoes-abertas': return 'bg-green-500 text-white';
      default: return 'bg-gray-300 text-gray-600';
    }
  }

  getSafeMapUrl(url: string): SafeResourceUrl {
    const embedUrl = url.replace('search/?api=1&query=', 'embed?q=');
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
  }
}